/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth0 } from '@auth0/auth0-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { CommandLoading } from 'cmdk';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { matchSorter } from 'match-sorter';
import type { CategoryType } from '~/components/common/ai-tools/aiReworkProductState';
import { promptByCategory } from '~/components/common/ai-tools/aiReworkProductState';
import { useCategories } from '~/components/common/sale/use-categories';
import {
  getSaleStatusColor,
  getSaleStatusLabel,
} from '~/components/common/sales-search/sales-search-view-item';
import { useDissociateImages } from '~/components/common/setup/hooks';
import useRenameColumn from '~/components/common/setup/setup-table-components/column-rename-utils';
import { crawlingProductsState } from '~/components/common/setup/states/crawlingProductState';
import {
  contextRowSelectionState,
  rowSelectionState,
  selectedRowsState,
  useResetContextSelection,
} from '~/components/common/setup/states/selectedRowsState';
import { stepState } from '~/components/common/setup/states/stepState';
import { CrawlingProductStatus, Steps } from '~/components/common/setup/types';
import { cn } from '~/components/ui/cn';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Dialog, DialogOverlay, DialogPortal } from '~/components/ui/dialog';
import { CalendarIcon } from '~/components/ui/icons/calendar';
import { CartIcon } from '~/components/ui/icons/cart';
import { EditIcon } from '~/components/ui/icons/edit';
import { SettingsIcon } from '~/components/ui/icons/settings';
import { SparklesIcon } from '~/components/ui/icons/sparkles';
import { SuitCaseIcon } from '~/components/ui/icons/suit-case';
import type {
  AiProductsInput,
  SalesElasticSearchHit,
} from '~/graphql/graphql.gen';
import {
  AiProductLanguageEnum,
  StoreRegion,
  useFormatProductsWithAiMutation,
  useSearchBrandQuery,
  useSearchSalesV2Query,
} from '~/graphql/graphql.gen';
import { track } from '~/libs/analytics';
import { uniqBy } from '~/utils/array';
import { convertUtcToLocaleDate, formatDate, truncateTime } from '~/utils/date';
import { formatPercentage } from '~/utils/number';
import { getSaleStatusForSale } from '~/utils/sale';

import { CommandBadge, CommandBadges } from './command-badge';
import type { Page } from './command-menu-state';
import {
  Pages,
  isOpenCommandMenuState,
  isSetupProductsActiveCommandMenuState,
  pagesCommandMenuState,
  selectedCustomFieldNameState,
} from './command-menu-state';

const CommandDialogContent: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Content>
> = ({ className, children, ...props }) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(
        'bg-background fixed inset-0 z-50 flex w-full items-start justify-center gap-4 border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
        className,
      )}
      style={{
        padding: 'calc(-0.23px + 13vh) 16px 16px',
      }}
      {...props}
    >
      <div className="flex min-w-min max-w-screen-sm flex-1">{children}</div>
    </DialogPrimitive.Content>
  </DialogPortal>
);
CommandDialogContent.displayName = DialogPrimitive.Content.displayName;

export const CommandMenu = () => {
  const commandRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollId = useRef<number>(null);
  const { resetContextSelection } = useResetContextSelection();

  const { t } = useTranslation();

  const [open, setOpen] = useAtom(isOpenCommandMenuState);
  const [inputValue, setInputValue] = useState('');
  const selectedFieldName = useAtomValue(selectedCustomFieldNameState);
  const pageStructure: Page[] = [
    {
      page: Pages.Home,
      placeholder: t('command.what_do_you_need'),
    },
    {
      page: Pages.SearchBrand,
      placeholder: t('command.search_brand_placeholder'),
      badge: t('command.search_brand'),
    },
    {
      page: Pages.SearchSale,
      placeholder: t('command.search_sale_placeholder'),
      badge: t('command.search_sale'),
    },
    {
      page: Pages.SetupProducts,
      placeholder: t('command.select_an_action'),
      badge: t('command.setup'),
    },
    {
      page: Pages.SetProductCategory,
      placeholder: t('command.search_category_placeholder'),
      badge: t('command.edit_category'),
    },
    {
      page: Pages.SetupProductsFormatDescription,
      placeholder: t('command.format_description_placeholder'),
      badge: t('command.format_description'),
    },
    {
      page: Pages.RenameCustomFieldColumn,
      placeholder: t('command.select_custom_field'),
      badge: t('command.rename_custom_field'),
    },
    {
      page: Pages.RenameCustomFieldInput,
      placeholder: t('command.rename_custom_field', {
        fieldName: selectedFieldName,
      }),
      badge: selectedFieldName ?? '',
    },
  ];
  const [pages, setPages] = useAtom(pagesCommandMenuState);
  const activePage = pages[pages.length - 1];
  const isHome = activePage === Pages.Home;
  const badges = pages.toSpliced(0, 1);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
        track('KeyboardShortcut', {
          label: 'Mod-k',
        });
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  const popPage = useCallback(() => {
    setPages((pages) => {
      const x = [...pages];
      x.splice(-1, 1);
      return x;
    });
  }, []);

  const bounce = () => {
    if (commandRef.current) {
      commandRef.current.style.transform = 'scale(0.96)';
      setTimeout(() => {
        if (commandRef.current) {
          commandRef.current.style.transform = '';
        }
      }, 100);

      setInputValue('');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);

        if (!open) {
          // Reset context selection if exists on close
          resetContextSelection();
        }
      }}
    >
      <CommandDialogContent className="overflow-hidden border-none p-0 shadow-lg">
        <Command
          ref={commandRef}
          shouldFilter={activePage === Pages.Home}
          className="[&_[cmdk-group-heading]]:text-muted-foreground transition-transform [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:size-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:size-5"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              bounce();
            }

            if (isHome || inputValue.length) {
              return;
            }

            if (e.key === 'Backspace') {
              e.preventDefault();
              popPage();
              bounce();
            }
          }}
        >
          {badges.length > 0 ? (
            <CommandBadges>
              {badges.map((badge) => (
                <CommandBadge key={badge}>
                  {pageStructure.find((p) => p.page === badge)?.badge}
                </CommandBadge>
              ))}
            </CommandBadges>
          ) : null}
          <CommandInput
            ref={inputRef}
            autoFocus
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value);

              //#region scroll list to top when typing
              // https://github.com/pacocoursey/cmdk/issues/234
              // https://github.com/pacocoursey/cmdk/issues/233

              // clear pending scroll
              if (scrollId.current) cancelAnimationFrame(scrollId.current);

              // the setTimeout is used to create a new task
              // this is to make sure that we don't scroll until the user is done typing
              // you can tweak the timeout duration ofc
              scrollId.current = requestAnimationFrame(() => {
                // inside your list select the first group and scroll to the top
                listRef.current?.scrollTo({ top: 0 });
              });

              //#endregion
            }}
            placeholder={
              pageStructure.find((p) => p.page === activePage)?.placeholder
            }
          />

          <CommandList ref={listRef}>
            {activePage === Pages.Home ? (
              <Home
                focusOnInput={() => {
                  inputRef.current?.focus();
                }}
                setOpen={setOpen}
              />
            ) : null}
            {activePage === Pages.SearchBrand ? (
              <Brands inputValue={inputValue} setOpen={setOpen} />
            ) : null}
            {activePage === Pages.SearchSale ? (
              <Sales inputValue={inputValue} setOpen={setOpen} />
            ) : null}
            {activePage === Pages.SetupProducts ? (
              <SetupProducts
                onPageChange={() => {
                  inputRef.current?.focus();
                }}
                setOpen={setOpen}
              />
            ) : null}
            {activePage === Pages.SetProductCategory ? (
              <SetProductCategory inputValue={inputValue} setOpen={setOpen} />
            ) : null}
            {activePage === Pages.SetupProductsFormatDescription ? (
              <SetupProductsFormatDescription
                inputValue={inputValue}
                setOpen={setOpen}
              />
            ) : null}
            {activePage === Pages.RenameCustomFieldColumn && (
              <RenameCustomFieldColumn inputValue={inputValue} />
            )}
            {activePage === Pages.RenameCustomFieldInput && (
              <RenameCustomFieldInput
                inputValue={inputValue}
                setOpen={setOpen}
              />
            )}
          </CommandList>
        </Command>
      </CommandDialogContent>
    </Dialog>
  );
};

const Home = ({
  focusOnInput,
  setOpen,
}: {
  focusOnInput: () => void;
  setOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pages, setPages] = useAtom(pagesCommandMenuState);
  const isSetupProductsActiveCommandMenu = useAtomValue(
    isSetupProductsActiveCommandMenuState,
  );

  return (
    <>
      <CommandEmpty>{t('command.no_result')}</CommandEmpty>
      <CommandGroup heading={t('command.sales')}>
        <CommandItem
          onSelect={() => {
            navigate({
              to: '/calendar',
              search: {
                category: [],
                csm: [],
                division: [],
                operationType: [],
                plugin: [],
                region: StoreRegion.Fr,
                sales: [],
                setup: [],
                tiering: [],
                topBrandType: [],
                type: [],
              },
            });
            setOpen(false);
          }}
        >
          <CalendarIcon className="size-4" />
          {t('command.calendar')}
        </CommandItem>
        <CommandItem
          onSelect={() => {
            setPages([...pages, Pages.SearchSale]);
            focusOnInput();
          }}
        >
          <CartIcon className="size-4" />
          {t('command.search_sale')}
        </CommandItem>
      </CommandGroup>
      <CommandGroup heading={t('command.brands')}>
        <CommandItem
          onSelect={() => {
            setPages([...pages, Pages.SearchBrand]);
            focusOnInput();
          }}
        >
          <SuitCaseIcon className="size-4" />
          {t('command.search_brand')}
        </CommandItem>
      </CommandGroup>
      {isSetupProductsActiveCommandMenu ? (
        <CommandGroup heading={t('command.setup')}>
          <CommandItem
            onSelect={() => {
              setPages([...pages, Pages.SetupProducts]);
              focusOnInput();
            }}
          >
            <SettingsIcon className="size-4" />
            {t('command.configure_products')}
          </CommandItem>
        </CommandGroup>
      ) : null}
    </>
  );
};

const SaleCommandItem = ({
  hit,
  storeRegion,
  onClose,
}: {
  hit: SalesElasticSearchHit;
  storeRegion: StoreRegion;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const status = getSaleStatusForSale(
    truncateTime(new Date(hit._source.start_at)).toString(),
    hit._source.end_at,
    hit._source.store_id,
    hit._source.hide,
    hit._source.is_cancelled,
  );

  return (
    <CommandItem
      key={hit?._id}
      onSelect={() => {
        navigate({
          to: '/vente/$saleId',
          params: {
            saleId: hit?._id,
          },
        });
        onClose();
      }}
      className="flex justify-between"
    >
      <div className="flex items-center space-x-2">
        <span>{hit?._source.brand_name}</span>
        <div className="size-1 rounded-full bg-gray-300"></div>
        <span className={cn('text-xs', getSaleStatusColor(status))}>
          {getSaleStatusLabel(status, t)}
        </span>

        <span className="sr-only">{storeRegion}</span>
      </div>

      {hit?._source?.start_at && hit?._source?.end_at ? (
        <div className="text-xs text-gray-500">
          {formatDate(
            new Date(hit._source.start_at ?? ''),
            'PP',
            i18n.language,
          )}{' '}
          -{' '}
          {formatDate(
            convertUtcToLocaleDate(
              hit._source.end_at ?? '',
              hit._source.store_id,
            ),
            'PP',
            i18n.language,
          )}
        </div>
      ) : null}
    </CommandItem>
  );
};

const Sales: React.FC<{
  inputValue: string;
  setOpen: (open: boolean) => void;
}> = ({ inputValue, setOpen }) => {
  const { t } = useTranslation();
  const searchParams = useSearch({
    strict: false,
  });

  const primaryStoreRegion =
    (searchParams.region as StoreRegion | undefined) ?? StoreRegion.Fr;
  const secondaryStoreRegion =
    primaryStoreRegion === StoreRegion.Fr ? StoreRegion.Us : StoreRegion.Fr;

  const { data: salesMain, isLoading: isLoadingMain } = useSearchSalesV2Query(
    { name: inputValue, store: primaryStoreRegion, limit: 25 },
    {
      select: (data) => data.searchSalesV2,
    },
  );
  const { data: salesSecondary, isLoading: isLoadingSecondary } =
    useSearchSalesV2Query(
      {
        name: inputValue,
        store: secondaryStoreRegion,
        limit: 25,
      },
      {
        select: (data) => data.searchSalesV2,
      },
    );

  return (
    <>
      {inputValue && !isLoadingMain && !isLoadingSecondary ? (
        <CommandEmpty>{t('command.no_result')}</CommandEmpty>
      ) : null}
      {(salesMain?.total ?? 0) > 0 ? (
        <CommandGroup
          heading={
            primaryStoreRegion === StoreRegion.Fr
              ? t('command.sales_eu')
              : t('command.sales_us')
          }
        >
          {salesMain?.hits
            ?.filter((hit) => !!hit)
            .map((hit) => (
              <SaleCommandItem
                key={hit?._id}
                hit={hit}
                storeRegion={primaryStoreRegion}
                onClose={() => setOpen(false)}
              />
            ))}
        </CommandGroup>
      ) : null}
      {(salesSecondary?.total ?? 0) > 0 ? (
        <CommandGroup
          heading={
            primaryStoreRegion === StoreRegion.Fr
              ? t('command.sales_us')
              : t('command.sales_eu')
          }
        >
          {salesSecondary?.hits
            ?.filter((hit) => !!hit)
            .map((hit) => (
              <SaleCommandItem
                key={hit?._id}
                hit={hit}
                storeRegion={secondaryStoreRegion}
                onClose={() => setOpen(false)}
              />
            ))}
        </CommandGroup>
      ) : null}
      {isLoadingMain || isLoadingSecondary ? (
        <CommandLoading className="py-4 text-center text-sm">
          {t('command.loading')}
        </CommandLoading>
      ) : null}
    </>
  );
};

const Brands: React.FC<{
  inputValue: string;
  setOpen: (open: boolean) => void;
}> = ({ inputValue, setOpen }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isLoading } = useSearchBrandQuery(
    {
      name: inputValue,
    },
    {
      select: (data) => data.searchBrand.slice(0, 100),
    },
  );

  return (
    <>
      {inputValue && !isLoading ? (
        <CommandEmpty>{t('command.no_result')}</CommandEmpty>
      ) : null}
      {(data?.length ?? 0) > 0 ? (
        <CommandGroup heading={t('command.brands')}>
          {data?.map((brand) => (
            <CommandItem
              key={brand.id}
              onSelect={() => {
                navigate({
                  to: '/marque/$brandId',
                  params: {
                    brandId: brand.id,
                  },
                });
                setOpen(false);
              }}
            >
              <span>
                {brand.name} <span className="sr-only">{brand.id}</span>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}
      {isLoading ? (
        <CommandLoading className="py-4 text-center text-sm">
          {t('command.loading')}
        </CommandLoading>
      ) : null}
    </>
  );
};

const SetupProducts = ({
  onPageChange,
  setOpen,
}: {
  onPageChange: () => void;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const dissociateImages = useDissociateImages();

  const [pages, setPages] = useAtom(pagesCommandMenuState);
  const setupPageStep = useAtomValue(stepState);

  return (
    <>
      <CommandEmpty>{t('command.no_result')}</CommandEmpty>
      <CommandGroup heading={t('command.setup')}>
        {setupPageStep === Steps.EDIT && (
          <>
            <CommandItem
              onSelect={() => {
                setPages([...pages, Pages.SetupProductsFormatDescription]);
                onPageChange();
              }}
            >
              <SparklesIcon className="size-4" />
              {t('command.format_description')}
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setPages([...pages, Pages.SetProductCategory]);
                onPageChange();
              }}
            >
              <EditIcon />
              {t('command.edit_category')}
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setPages([...pages, Pages.RenameCustomFieldColumn]);
                onPageChange();
              }}
            >
              <EditIcon />
              {t('command.rename_custom_field')}
            </CommandItem>
          </>
        )}
        <CommandItem
          onSelect={() => {
            dissociateImages();
            setOpen(false);
          }}
        >
          <EditIcon />
          {t('command.dissociate_images')}
        </CommandItem>
      </CommandGroup>
    </>
  );
};

const SetProductCategory: React.FC<{
  inputValue: string;
  setOpen: (open: boolean) => void;
}> = ({ inputValue, setOpen }) => {
  const { i18n, t } = useTranslation();

  const { data, isLoading } = useCategories();

  const rowSelection = useAtomValue(rowSelectionState);
  const contextRowSelection = useAtomValue(contextRowSelectionState);
  const setCrawlingProducts = useSetAtom(crawlingProductsState);

  const flattenedCategories =
    data?.flatMap((category) => [
      category,
      ...(category.subCategories?.map((subCategory) => ({
        ...subCategory,
        label: `${category.label} > ${subCategory.label}`,
      })) ?? []),
    ]) ?? [];
  const filteredCategories =
    inputValue.trim() === ''
      ? flattenedCategories
      : matchSorter(flattenedCategories, inputValue, {
          keys: ['label'],
        });

  const onSetProductCategory = (categoryId: string) => {
    const productRowIndexes =
      Object.keys(rowSelection).length > 0
        ? Object.keys(rowSelection).map((key) =>
            parseInt(key.split('.')[0] ?? '', 10),
          )
        : contextRowSelection
          ? [parseInt(contextRowSelection.split('.')[0] ?? '', 10)]
          : null;

    setCrawlingProducts((previous) => {
      if (!previous) return;

      const products = structuredClone(previous);

      if (productRowIndexes) {
        productRowIndexes.forEach((productRowIndex) => {
          if (!products.products[productRowIndex]) return;
          products.products[productRowIndex] = {
            ...products.products[productRowIndex],
            category: categoryId,
            _subRows: [
              ...(products.products[productRowIndex]._subRows ?? []).map(
                (subProduct) => ({
                  ...subProduct,
                  category: categoryId,
                }),
              ),
            ],
          };
        });
      } else {
        products.products.forEach((product) => {
          product.category = categoryId;
          product._subRows?.forEach((subProduct) => {
            subProduct.category = categoryId;
          });
        });
      }

      return products;
    });
  };

  return (
    <>
      {inputValue && !isLoading && filteredCategories.length === 0 ? (
        <CommandEmpty>{t('command.no_result')}</CommandEmpty>
      ) : null}
      {filteredCategories.length > 0 ? (
        <CommandGroup heading={t('command.categories')}>
          {filteredCategories?.map((category) => (
            <CommandItem
              key={category.id}
              onSelect={() => {
                onSetProductCategory(category.id);
                setOpen(false);
              }}
              className="flex justify-between space-x-4"
            >
              <span>
                {category.label} <span className="sr-only">{category.id}</span>
              </span>
              <span className="text-xs text-gray-500">
                {formatPercentage((category.tva ?? 0) / 100, i18n.language)}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}
      {isLoading ? (
        <CommandLoading className="py-4 text-center text-sm">
          {t('command.loading')}
        </CommandLoading>
      ) : null}
    </>
  );
};

const SetupProductsFormatDescription: React.FC<{
  inputValue: string;
  setOpen: (open: boolean) => void;
}> = ({ inputValue, setOpen }) => {
  const { t } = useTranslation();

  const { user } = useAuth0();

  const rowSelection = useAtomValue(rowSelectionState);
  const contextRowSelection = useAtomValue(contextRowSelectionState);
  const selectedRows = useAtomValue(selectedRowsState);
  const setCrawlingProducts = useSetAtom(crawlingProductsState);

  const { mutateAsync: mutateFormatProductsWithAi } =
    useFormatProductsWithAiMutation();

  const flattenedCategories = Object.entries(promptByCategory).map(
    ([key, value]) => ({
      label: value.label,
      id: key as CategoryType,
    }),
  );

  const filteredCategories =
    inputValue.trim() === ''
      ? flattenedCategories
      : matchSorter(flattenedCategories, inputValue, {
          keys: ['label'],
        });

  const onSetupProductsFormatDescription = (categoryId: CategoryType) => {
    const productRowIndexes =
      Object.keys(rowSelection).length > 0
        ? Object.keys(rowSelection).map((key) =>
            parseInt(key.split('.')[0] ?? '', 10),
          )
        : contextRowSelection
          ? [parseInt(contextRowSelection.split('.')[0] ?? '', 10)]
          : null;

    if (Object.keys(rowSelection).length === 0) return;

    const input: AiProductsInput = {
      lang: AiProductLanguageEnum.Fr,
      prompt: promptByCategory[categoryId].promptFr,
      type: categoryId,
      sessionId: `reformat-product-${user?.sub ?? ''}`,
      products: uniqBy(selectedRows, (row) => row.name).map((row) => ({
        id: row.name,
        name: row.name,
        description: row.description ?? '',
      })),
      withNames: true,
    };
    mutateFormatProductsWithAi({
      input,
    });

    setCrawlingProducts((previous) => {
      if (!previous) return;

      const products = structuredClone(previous);

      if (productRowIndexes) {
        productRowIndexes.forEach((productRowIndex) => {
          if (!products.products[productRowIndex]) return;
          products.products[productRowIndex] = {
            ...products.products[productRowIndex],
            _status: CrawlingProductStatus.IN_PROGRESS,
            _subRows: [
              ...(products.products[productRowIndex]._subRows ?? []).map(
                (subProduct) => ({
                  ...subProduct,
                  _status: CrawlingProductStatus.IN_PROGRESS,
                }),
              ),
            ],
          };
        });
      }

      return products;
    });
  };

  return (
    <>
      {filteredCategories.length > 0 && (
        <CommandGroup heading={t('command.format_description_categories')}>
          {filteredCategories?.map((category) => (
            <CommandItem
              key={category.id}
              onSelect={() => {
                onSetupProductsFormatDescription(category.id);
                setOpen(false);
              }}
              className="flex justify-between space-x-4"
            >
              <span>
                {category.label}{' '}
                <span className="sr-only">{category.label}</span>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </>
  );
};

const RenameCustomFieldColumn: React.FC<{
  inputValue: string;
}> = ({ inputValue }) => {
  const { t } = useTranslation();
  const crawlingProducts = useAtomValue(crawlingProductsState);
  const [pages, setPages] = useAtom(pagesCommandMenuState);
  const setSelectedFieldName = useSetAtom(selectedCustomFieldNameState);

  const flattenedCustomFields = Array.from(
    new Set(
      crawlingProducts?.products.flatMap((product) => {
        if (!product.customFields) return [];
        return product.customFields.map((field) => field.name);
      }) ?? [],
    ),
  ).map((name) => ({
    name,
    index: crawlingProducts?.products[0]?.customFields?.findIndex(
      (field) => field.name === name,
    ),
  }));

  const filteredCustomFields =
    inputValue.trim() === ''
      ? flattenedCustomFields
      : matchSorter(flattenedCustomFields, inputValue, {
          keys: ['name'],
        });

  return (
    <CommandGroup heading={t('command.rename_custom_field')}>
      {filteredCustomFields.length > 0 ? (
        filteredCustomFields.map(({ name, index }) => (
          <CommandItem
            key={index}
            onSelect={() => {
              setSelectedFieldName(name);
              setPages([...pages, Pages.RenameCustomFieldInput]);
            }}
            className="flex justify-between space-x-4"
          >
            {name}
          </CommandItem>
        ))
      ) : (
        <CommandEmpty>{t('command.no_custom_fields')}</CommandEmpty>
      )}
    </CommandGroup>
  );
};

const RenameCustomFieldInput: React.FC<{
  inputValue: string;
  setOpen: (open: boolean) => void;
}> = ({ inputValue, setOpen }) => {
  const { t } = useTranslation();
  const { modifyCustomFieldName } = useRenameColumn();
  const [selectedFieldName, setSelectedFieldName] = useAtom(
    selectedCustomFieldNameState,
  );

  const crawlingProducts = useAtomValue(crawlingProductsState);

  const selectedFieldIndex = () =>
    crawlingProducts?.products[0]?.customFields?.findIndex(
      (field) => field?.name === selectedFieldName,
    ) ?? -1;

  const allCustomFieldNames = () =>
    crawlingProducts?.products.flatMap((product) =>
      product.customFields?.map((field) => field?.name),
    ) ?? [];

  const isValid = () =>
    inputValue && !allCustomFieldNames().includes(inputValue);

  const index = selectedFieldIndex();

  if (index === -1) {
    return;
  }

  const handleRename = (onSelectValue: string) => {
    modifyCustomFieldName(index, onSelectValue)();
    setSelectedFieldName(onSelectValue);
    setOpen(false);
  };

  return (
    <div>
      {allCustomFieldNames().includes(inputValue) && (
        <p className="m-3 text-xs text-red-600">
          {t('command.custom_field_name_taken')}
        </p>
      )}
      <CommandGroup
        heading={t('command.enter_new_name', {
          fieldName: selectedFieldName,
        })}
      >
        <CommandItem
          key={index}
          onSelect={() => {
            if (isValid()) handleRename(inputValue);
          }}
          className="flex justify-between space-x-4"
        >
          {inputValue
            ? `${selectedFieldName} ⮕ ${inputValue}`
            : `${selectedFieldName}`}
        </CommandItem>
      </CommandGroup>
    </div>
  );
};
